import { Alert, Descriptions, Drawer, Empty, Spin, Tag, Typography, theme } from 'antd';
import { useEffect, useState } from 'react';
import { http } from '../../api/client.js';
import { tx } from '../../i18n/translate.js';
import type { AuditRow } from './audit-log.types.js';

interface Props {
  auditId: string | null;
  onClose: () => void;
}

export function AuditLogDetailDrawer({ auditId, onClose }: Props) {
  const { token } = theme.useToken();
  const [row, setRow] = useState<AuditRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!auditId) {
      setRow(null);
      setError(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError(false);
    http
      .get<AuditRow>(`/audit-log/${auditId}`)
      .then((response) => {
        if (active) setRow(response.data);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [auditId]);

  return (
    <Drawer
      title={tx('hardcoded:pages.admin.auditlogpage.detail_title')}
      open={auditId !== null}
      onClose={onClose}
      width={720}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {error ? (
          <Alert
            type="error"
            showIcon
            message={tx('hardcoded:pages.admin.auditlogpage.detail_failed')}
            description={tx('hardcoded:pages.admin.auditlogpage.detail_failed_help')}
          />
        ) : row ? (
          <>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label={tx('hardcoded:pages.admin.auditlogpage.9438cf9a')}>
                {new Date(row.occurred_at).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label={tx('hardcoded:pages.admin.auditlogpage.c2706623')}>
                {row.user_name ?? tx('hardcoded:pages.admin.auditlogpage.system_actor')}
              </Descriptions.Item>
              <Descriptions.Item label={tx('hardcoded:pages.admin.auditlogpage.bcc23db9')}>
                <Typography.Text code copyable>
                  {row.event_type}
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label={tx('hardcoded:pages.admin.auditlogpage.target')}>
                {[row.entity_name, row.entity_id ?? row.target_ref].filter(Boolean).join(' / ') ||
                  '—'}
              </Descriptions.Item>
              <Descriptions.Item label={tx('hardcoded:pages.admin.auditlogpage.request')}>
                {[row.request_method, row.request_path].filter(Boolean).join(' ') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={tx('hardcoded:pages.admin.auditlogpage.request_id')}>
                <Typography.Text copyable={Boolean(row.request_id)}>
                  {row.request_id ?? '—'}
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label={tx('hardcoded:pages.admin.auditlogpage.correlation_id')}>
                <Typography.Text copyable={Boolean(row.correlation_id)}>
                  {row.correlation_id ?? '—'}
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label={tx('hardcoded:pages.admin.auditlogpage.source')}>
                {[row.ip, row.client_platform, row.device_id].filter(Boolean).join(' · ') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={tx('hardcoded:pages.admin.auditlogpage.7b61a3fd')}>
                <Tag color={row.result === 'success' ? 'green' : 'red'}>{row.result}</Tag>
                {row.error_message ? ` ${row.error_message}` : null}
              </Descriptions.Item>
            </Descriptions>
            <Evidence
              title={tx('hardcoded:pages.admin.auditlogpage.before')}
              value={row.before_data}
              background={token.colorFillQuaternary}
            />
            <Evidence
              title={tx('hardcoded:pages.admin.auditlogpage.after')}
              value={row.after_data}
              background={token.colorFillQuaternary}
            />
          </>
        ) : (
          <Empty description={tx('hardcoded:pages.admin.auditlogpage.85a232f3')} />
        )}
      </Spin>
    </Drawer>
  );
}

function Evidence({
  title,
  value,
  background,
}: { title: string; value: unknown; background: string }) {
  return (
    <section style={{ marginTop: 20 }}>
      <Typography.Title level={5}>{title}</Typography.Title>
      <Typography.Paragraph copyable={value !== null && value !== undefined} style={{ margin: 0 }}>
        <pre style={{ margin: 0, padding: 12, overflow: 'auto', background }}>
          {value === null || value === undefined ? '—' : JSON.stringify(value, null, 2)}
        </pre>
      </Typography.Paragraph>
    </section>
  );
}
